import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Star, X } from "lucide-react";
import { toast } from "sonner";
import { fileUrl, uploadFile } from "@/lib/upload";
import { api, errMsg } from "@/lib/api";
import { MediaGrid } from "@/components/member/Feed";

const MAX = 10;

export function useImageUrl(id) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    setUrl(null);
    if (id) fileUrl(id).then(setUrl).catch(() => {});
  }, [id]);
  return url;
}

export function MainImage({ id, className = "" }) {
  const url = useImageUrl(id);
  if (!id) return null;
  return <div className={`overflow-hidden bg-mr-raised ${className}`} data-testid="main-image">{url && <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />}</div>;
}

export function ImageGallery({ ids }) {
  if (!ids?.length) return null;
  return <div data-testid="image-gallery"><MediaGrid media={ids.map((id) => ({ id, kind: "photo" }))} /></div>;
}

function Thumb({ id, main, onMain, onLeft, onRight, onRemove }) {
  const url = useImageUrl(id);
  return (
    <div className={`relative w-28 h-28 shrink-0 rounded-[3px] overflow-hidden border ${main ? "border-mr-accent" : "border-mr-border"} bg-mr-raised`} data-testid="image-manager-item">
      {url && <img src={url} alt="" className="w-full h-full object-cover" />}
      {main && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-mr-accent text-black font-medium" data-testid="image-manager-main-badge">Principale</span>}
      <button type="button" aria-label="Retirer" data-testid="image-manager-remove" onClick={onRemove} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/70 text-white"><X size={13} /></button>
      <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/60 text-white">
        <button type="button" aria-label="Vers la gauche" data-testid="image-manager-left" onClick={onLeft} className="w-8 h-7 grid place-items-center"><ChevronLeft size={14} /></button>
        {!main && <button type="button" aria-label="Image principale" data-testid="image-manager-set-main" onClick={onMain} className="w-8 h-7 grid place-items-center"><Star size={13} /></button>}
        <button type="button" aria-label="Vers la droite" data-testid="image-manager-right" onClick={onRight} className="w-8 h-7 grid place-items-center"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export function ImagesEditor({ ids, endpoint, onSaved, testId = "images-editor" }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(ids || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(endpoint, { file_ids: value });
      toast.success("Images enregistrées");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!open) return <button type="button" data-testid={`${testId}-open`} className="mr-btn-secondary" onClick={() => { setValue(ids || []); setOpen(true); }}><ImagePlus size={15} /> {ids?.length ? "Modifier les images" : "Ajouter des images"}</button>;
  return (
    <div className="mr-card space-y-3" data-testid={testId}>
      <p className="font-medium">Images</p>
      <ImageManager value={value} onChange={setValue} />
      <div className="flex gap-2"><button type="button" data-testid={`${testId}-save`} className="mr-btn-primary" disabled={busy} onClick={save}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        <button type="button" className="mr-btn-ghost" onClick={() => setOpen(false)}>Annuler</button></div>
    </div>
  );
}

export function ImageManager({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const [over, setOver] = useState(false);
  const add = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    if (value.length + list.length > MAX) return toast.error(`${MAX} images maximum.`);
    setBusy(list.length);
    const ids = [];
    for (const f of list) {
      try {
        ids.push(await uploadFile("photo", f));
      } catch {
        toast.error(`Échec de l'envoi : ${f.name}`);
      }
      setBusy((n) => n - 1);
    }
    onChange([...value, ...ids]);
  };
  const move = (k, d) => {
    const j = k + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[k], next[j]] = [next[j], next[k]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="image-manager">
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        onClick={() => ref.current.click()} role="button" tabIndex={0} data-testid="image-manager-dropzone"
        className={`border border-dashed rounded-[3px] p-6 text-center cursor-pointer transition-colors ${over ? "border-mr-accent bg-mr-raised" : "border-mr-border hover:border-mr-text2"}`}>
        <ImagePlus size={22} className="mx-auto text-mr-accent" />
        <p className="text-sm mt-2">Glissez vos images ici ou <span className="text-mr-accent">choisissez-les</span></p>
        <p className="text-xs text-mr-muted mt-1">Galerie ou appareil photo sur téléphone · {value.length}/{MAX} images{busy ? ` · envoi de ${busy}…` : ""}</p>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid="image-manager-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {value.length > 0 && <div className="flex gap-2 overflow-x-auto pb-1">{value.map((id, k) => (
        <Thumb key={id} id={id} main={k === 0} onMain={() => onChange([id, ...value.filter((x) => x !== id)])} onLeft={() => move(k, -1)} onRight={() => move(k, 1)} onRemove={() => onChange(value.filter((x) => x !== id))} />))}</div>}
      {value.length > 1 && <p className="text-xs text-mr-muted">La première image est l'image principale. Utilisez l'étoile ou les flèches pour changer l'ordre.</p>}
    </div>
  );
}
